import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { asRelativeDate } from "@/utils/format";
import { sanitizeStudyRoute } from "@/utils/study-route";
import { ProjectsLayout } from "@/components/layouts";
import { Study } from "@/api/studio/studies/types/study.type";
import { ProjectTableSkeleton } from "@/features/projects/components/project-table-skeleton";
import { CreateStudyModal } from "@/features/studies/modals/create-study-modal";
import { useModalStore } from "@/stores/use-modal-store";
import { useGetProject } from "@/api/studio/projects/queries";
import { useGetProjectStudies } from "@/api/studio/studies/queries";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ArchiveBoxIcon, UsersIcon, LockClosedIcon } from "@heroicons/react/24/solid";
import { ProjectDropdown } from "@/features/projects/components/project-dropdown";
import { StudyDropdown } from "@/features/studies/components/study-dropdown";
import { FolderIcon } from "@heroicons/react/20/solid";
import { Button, ButtonSkeleton } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { Table, TableHeader, TableCell, TableRow } from "@/components/ui/table";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { TextSkeleton } from "@/features/projects/components/text-skeleton";
import { EmptyState } from "@/features/projects/components/empty-state";
import { StudyIcon } from "@/features/studies/components/study-icon";
import { dynamicUseCase } from "@/utils/dynamic-use-case";
import { GetStudiesReq } from "@/api/studio/studies/api";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { SelectSort } from "@/components/ui/select";

/*
 * ProjectDetails component
 */
export const ProjectDetails = ({ id }: { id: string }) => {
  /* Stores */
  const [, navigate] = useLocation();
  const { open } = useModalStore((state) => state.modalActions);
  const theme = useGlobalStore((state) => state.theme);

  /* Hooks */
  const { segmentPageEvent, segmentTrackEvent } = useSegment();

  /* State */
  const [view, setView] = useState<GetStudiesReq["view"]>("all");
  const [orderBy, setOrderBy] = useState<GetStudiesReq["orderBy"]>("updatedAt");
  const [sortBy, setSortBy] = useState<GetStudiesReq["sortBy"]>("desc");
  const [pageIndex, setPageIndex] = useState(1);

  /* Queries */
  const { data: project, isLoading: projectLoading } = useGetProject(id);
  const { data: studies, isLoading: studiesLoading } = useGetProjectStudies(id, { pageIndex, itemsPerPage: 10, sortBy, orderBy, view });

  /* Handlers */
  const handleCreateStudy = () => {
    // Segment tracking
    segmentTrackEvent("Study Creation Initiated", { projectId: id });

    // open modal
    open(<CreateStudyModal projectId={id} />);
  };

  const handleOpenStudy = (study: Study) => {
    const validRoute = sanitizeStudyRoute(study.route);
    navigate(`/projects/${id}/studies/${study.id}/${study.type}/${validRoute}`);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the project page
    if (project && theme && !projectLoading)
      segmentPageEvent("Project", {
        title: `${project?.name} | Dragonfly AI Studio`,
        theme: theme,
      });
  }, [project, segmentPageEvent, theme, projectLoading]);

  /* Render */
  return (
    <ProjectsLayout>
      {/* Primary */}
      <Toolbar>
        <ToolbarItem>
          {projectLoading && <TextSkeleton variant="h1" />}
          {!projectLoading && <h1 className="font-semibold max-w-[50vw] truncate">{project?.name}</h1>}
          {projectLoading && <ButtonSkeleton />}
          {!projectLoading && (
            <Button variant="ghost" className="text-sm pointer-events-none">
              {project?.visibility === "private" && (
                <>
                  <LockClosedIcon className="size-3.5" /> Private
                </>
              )}
              {project?.visibility === "public" && (
                <>
                  <UsersIcon className="size-3.5" /> Shared
                </>
              )}
            </Button>
          )}
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" onClick={handleCreateStudy}>
            <PlusIcon className="size-5" />
            New Study
          </Button>
          {project && <ProjectDropdown project={project} />}
        </ToolbarItem>
      </Toolbar>

      {/* Secondary */}
      <Toolbar>
        <ToolbarItem>
          <Button selected={view === "all"} onClick={() => setView("all")}>
            All
          </Button>
          <Button selected={view === "archived"} onClick={() => setView("archived")}>
            <ArchiveBoxIcon className="size-3.5" />
            Archived
          </Button>
        </ToolbarItem>
        <ToolbarItem>
          <SelectSort setOrderBy={setOrderBy} setSortBy={setSortBy} />
        </ToolbarItem>
      </Toolbar>

      {/* Tertiary */}
      <Toolbar>
        <ToolbarItem>
          {studiesLoading && <TextSkeleton variant="h2" />}
          {!studiesLoading && (
            <h2>
              {studies?.maxRecordCount} {studies?.maxRecordCount && studies?.maxRecordCount < 2 ? "study" : "studies"}
            </h2>
          )}
        </ToolbarItem>
      </Toolbar>

      {/* Skeleton loader */}
      {studiesLoading && <ProjectTableSkeleton />}

      {/* Empty fallback */}
      {!studiesLoading && studies?.studies.length === 0 && <EmptyState title="This project has no studies" />}

      {/* Studies */}
      {!studiesLoading && !!studies?.studies.length && (
        <Table>
          <thead>
            <TableRow>
              <TableHeader className="w-80">Study</TableHeader>
              <TableHeader>Project</TableHeader>
              <TableHeader>Use case</TableHeader>
              <TableHeader>Author</TableHeader>
              <TableHeader>Last edited</TableHeader>
              <TableHeader className="w-40"></TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {studies?.studies.map((study) => (
              <TableRow key={study.id} onClick={() => handleOpenStudy(study)}>
                <TableCell>
                  <StudyIcon useCase={study.useCase} />
                  <div className="flex flex-col gap-1 truncate">
                    <p className="font-semibold truncate">{study.name}</p>
                    <p className="text-sm text-base-content/60 truncate">
                      {study.assetCount} {study.assetCount === 1 ? study.type : `${study.type}s`}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <FolderIcon className="size-4 shrink-0" />
                  <p className="text-sm truncate">{study.project.name}</p>
                </TableCell>
                <TableCell>
                  {dynamicUseCase(study.useCase.name)}
                  <p className="text-sm truncate">{study.useCase.name}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm truncate">{study.username}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm truncate">{asRelativeDate(study.updatedAt)}</p>
                </TableCell>
                <TableCell className="justify-end" truncate={false}>
                  <StudyDropdown projectId={study.project.id} study={study} />
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}

      {/* Pagination */}
      <Pagination currentPage={pageIndex} totalPages={studies?.maxPageIndex ?? 1} onPageChange={setPageIndex} />
    </ProjectsLayout>
  );
};
